import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { money } from "@/lib/format";
import {
  PageHeader,
  Loader,
  EmptyState,
  KpiCard,
} from "@/components/common/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Wallet, Plus, CalendarDays, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const CATS = [
  "Rent",
  "Electricity",
  "Gas",
  "Staff Salary",
  "Maintenance",
  "Marketing",
  "Supplies",
  "Other",
];

const getToday = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatExpenseDate = (date) => {
  if (!date) return "—";

  const value = String(date);

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-");
    return `${day}/${month}/${year}`;
  }

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "—";

  return parsed.toLocaleDateString("en-IN");
};

const getExpenseDateKey = (date) => {
  if (!date) return "";

  const value = String(date);

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "";

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const INITIAL_FORM = {
  category: "Rent",
  amount: "",
  payment_method: "Cash",
  notes: "",
  date: getToday(),
};

export default function Expenses() {
  const { branchId, branches } = useApp();

  const activeBranch =
    branchId === "all" ? branches[0]?.id : branchId;

  const today = getToday();

  const [list, setList] = useState(null);

  // null means "All dates". A date string means that date is selected.
  // Default is today.
  const [selectedDate, setSelectedDate] = useState(today);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);

  const load = () => {
    api
      .expenses(branchId)
      .then((r) => setList(Array.isArray(r.data) ? r.data : []))
      .catch(() => setList([]));
  };

  useEffect(() => {
    setList(null);
    load();
  }, [branchId]);

  const filteredList = useMemo(() => {
    if (!selectedDate) {
      return list || [];
    }

    return (list || []).filter(
      (expense) => getExpenseDateKey(expense.date) === selectedDate
    );
  }, [list, selectedDate]);

  const total = useMemo(
    () =>
      filteredList.reduce(
        (sum, expense) => sum + Number(expense.amount || 0),
        0
      ),
    [filteredList]
  );

  const create = async () => {
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (!form.date) {
      toast.error("Please select an expense date");
      return;
    }

    if (!activeBranch) {
      toast.error("Please select a branch");
      return;
    }

    try {
      await api.createExpense({
        ...form,
        amount: Number(form.amount),
        branch_id: activeBranch,

        // Preserve the selected calendar date without UTC date shifting.
        date: new Date(`${form.date}T12:00:00`).toISOString(),
      });

      toast.success("Expense added successfully");

      setOpen(false);

      setForm({
        ...INITIAL_FORM,
        date: selectedDate || today,
      });

      load();
    } catch (e) {
      toast.error("Failed to add expense");
    }
  };

  const openAddExpense = () => {
    setForm({
      ...INITIAL_FORM,
      // If All is selected, new expense defaults to today's date.
      date: selectedDate || today,
    });

    setOpen(true);
  };

  const resetDate = () => {
    setSelectedDate(today);
  };

  const showAll = () => {
    setSelectedDate(null);
  };

  if (!list) {
    return (
      <div className="p-6">
        <Loader label="Loading expenses" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 animate-fade-up">
      <PageHeader
        title="Expenses"
        subtitle="Track restaurant spending"
        actions={
          <Button
            onClick={openAddExpense}
            className="rounded-xl font-semibold"
            data-testid="exp-add"
          >
            <Plus size={16} className="mr-1" />
            Add Expense
          </Button>
        }
      />

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          label="Total Expenses"
          value={money(total)}
          icon={Wallet}
          accent="orange"
        />

        <KpiCard
          label="Entries"
          value={filteredList.length}
          icon={Wallet}
          accent="sky"
        />
      </div>

      {/* Table toolbar: result information on the left,
          calendar + reset/all controls on the right. */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="text-sm text-muted-foreground">
          {selectedDate ? (
            <>
              Showing expenses for{" "}
              <span className="font-semibold text-foreground">
                {formatExpenseDate(selectedDate)}
              </span>
            </>
          ) : (
            <>
              Showing{" "}
              <span className="font-semibold text-foreground">
                all expenses
              </span>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 flex-wrap">
          <div className="relative">
            <CalendarDays
              size={17}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
            />
            <Input
              type="date"
              value={selectedDate || ""}
              max={today}
              onChange={(e) =>
                setSelectedDate(e.target.value || null)
              }
              className="h-10 rounded-xl pl-10 w-[170px]"
              aria-label="Filter expenses by date"
              data-testid="exp-date-filter"
            />
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={resetDate}
            className="h-10 rounded-xl"
            title="Reset to today"
            data-testid="exp-date-reset"
          >
            <RotateCcw size={15} className="mr-1.5" />
            Reset
          </Button>

          <Button
            type="button"
            variant={!selectedDate ? "default" : "outline"}
            onClick={showAll}
            className="h-10 rounded-xl"
            data-testid="exp-date-all"
          >
            All
          </Button>
        </div>
      </div>

      {/* Empty State */}
      {filteredList.length === 0 ? (
        <div className="min-h-[420px] rounded-2xl border border-border bg-card flex items-center justify-center">
          <EmptyState
            icon={Wallet}
            title={
              selectedDate
                ? "No expenses for this date"
                : "No expenses yet"
            }
            subtitle={
              selectedDate
                ? "Record an expense for the selected date to keep track of spending."
                : "Record your restaurant expenses to keep track of spending."
            }
            action={
              <Button
                onClick={openAddExpense}
                className="rounded-xl font-semibold mt-3"
                data-testid="exp-empty-add"
              >
                <Plus size={16} className="mr-1" />
                Add Expense
              </Button>
            }
          />
        </div>
      ) : (
        <div className="rounded-2xl bg-card border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-muted-foreground">
                <tr className="text-center">
                  {[
                    "Category",
                    "Amount",
                    "Method",
                    "Date",
                    "Notes",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-xs font-semibold uppercase tracking-wider whitespace-nowrap text-center"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {filteredList.map((e) => (
                  <tr
                    key={e.id}
                    className="border-t border-border hover:bg-secondary/30 transition-colors text-center"
                  >
                    <td className="px-4 py-3 font-semibold text-center">
                      {e.category}
                    </td>

                    <td className="px-4 py-3 font-bold text-center">
                      {money(e.amount)}
                    </td>

                    <td className="px-4 py-3 text-muted-foreground text-center">
                      {e.payment_method}
                    </td>

                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap text-center">
                      {formatExpenseDate(e.date)}
                    </td>

                    <td className="px-4 py-3 text-muted-foreground text-center">
                      {e.notes || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Expense Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white sm:max-w-sm"
          data-testid="exp-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display">
              Add Expense
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {/* Expense Date */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">
                Expense Date
              </label>

              <div className="relative">
                <CalendarDays
                  size={17}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                />
                <Input
                  type="date"
                  value={form.date}
                  max={today}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      date: e.target.value,
                    })
                  }
                  className="h-11 rounded-xl pl-10"
                  data-testid="exp-date"
                />
              </div>
            </div>

            {/* Category */}
            <select
              value={form.category}
              onChange={(e) =>
                setForm({
                  ...form,
                  category: e.target.value,
                })
              }
              className="w-full h-11 rounded-xl border border-border bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-primary/20"
              data-testid="exp-category"
            >
              {CATS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>

            {/* Amount */}
            <Input
              type="number"
              min="0"
              placeholder="Amount ₹"
              value={form.amount}
              onChange={(e) =>
                setForm({
                  ...form,
                  amount: e.target.value,
                })
              }
              className="h-11 rounded-xl"
              data-testid="exp-amount"
            />

            {/* Payment Method */}
            <select
              value={form.payment_method}
              onChange={(e) =>
                setForm({
                  ...form,
                  payment_method: e.target.value,
                })
              }
              className="w-full h-11 rounded-xl border border-border bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-primary/20"
              data-testid="exp-payment-method"
            >
              {["Cash", "UPI", "Card", "Bank"].map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>

            {/* Notes */}
            <Input
              placeholder="Notes (optional)"
              value={form.notes}
              onChange={(e) =>
                setForm({
                  ...form,
                  notes: e.target.value,
                })
              }
              className="h-11 rounded-xl"
              data-testid="exp-notes"
            />
          </div>

          <DialogFooter>
            <Button
              onClick={create}
              className="w-full h-11 rounded-xl font-semibold"
              data-testid="exp-save"
            >
              Save Expense
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
